import lodash, { get, keys } from 'lodash';
import { FieldErrors } from 'react-hook-form';
import { v4 as uuid } from 'uuid';

import { ICountry } from '@/pages/project-management/detail/utils';
import { SCENARIO_TYPES } from '@pages/project-management/add-edit/contants';
import { DEFAULT_FORM_OBSERVER } from '@pages/project-management/add-edit/contants/projectExternalProxy';
import { IProjectExternal, IProjectFamily, IProjectStakeholder } from '@pages/project-management/add-edit/models';
import { FormObserver } from '@pages/project-management/add-edit/models/projectExternalProxy';
import { IStepFive, IStepFour, IStepSix, IStepThree, IStepTwo } from '@pages/project-management/add-edit/models/scenarioB';

import { convertObjDatetoString, convertStringToObjDate } from '@/components/common/date-input';

import { APPLICANT_GENDER } from '@/utils/constants';
import { EMAIL_REGEX, LOCALSTORAGE, UPLOAD_STATUS } from '@/utils/constants/AppConstants';
import { DataInput, DataViewer, isJsonString } from '@/utils/helpers/common';

import { relativePath } from '../utils';
import {
  DEFAULT_SCENARIO_B_STEP_2,
  DEFAULT_SCENARIO_B_STEP_3,
  DEFAULT_SCENARIO_B_STEP_4,
  DEFAULT_SCENARIO_B_STEP_5,
  DEFAULT_SCENARIO_B_STEP_6,
  DEFAULT_SCENARIO_B_STEP_7,
  DEFAULT_SCENARIO_B_STEP_STATUS,
  STEP_JUMP
} from './constants';
import { INVOLVED_MEMBERS, REQUIRED_STAKEHOLDER_FIELDS } from './form/step-five/constants';
import { LOCAL_REPRESENTATIVE, REQUIRED_LOCAL_REPRESENTATIVE_FIELDS } from './form/step-four/constants';
import { REQUIRED_FAMILY_FIELDS } from './form/step-three/constants';
import { REQUIRED_APPLICANT_FIELDS, REQUIRED_ASSIGNMENT_FIELDS } from './form/step-two/constants';

export const getUserProfile = () => {
  const user = localStorage.getItem(LOCALSTORAGE.USER) ?? '';
  return isJsonString(user) ? JSON.parse(user) : null;
};

export const convertResponseForBType = (data: IProjectExternal) => {
  const formDefault = { ...DEFAULT_FORM_OBSERVER, scenarioType: SCENARIO_TYPES.SCENARIO_B };
  formDefault.steps = [
    { ...DEFAULT_SCENARIO_B_STEP_2 },
    { ...DEFAULT_SCENARIO_B_STEP_3 },
    { ...DEFAULT_SCENARIO_B_STEP_4 },
    { ...DEFAULT_SCENARIO_B_STEP_5 },
    { ...DEFAULT_SCENARIO_B_STEP_6 },
    { ...DEFAULT_SCENARIO_B_STEP_7 }
  ];
  const cloneDefaultStepStatus = DEFAULT_SCENARIO_B_STEP_STATUS.map((item) => ({ ...item }));
  formDefault.stepStatus = [...DEFAULT_FORM_OBSERVER.stepStatus, ...cloneDefaultStepStatus];

  if (data) {
    formDefault.selectedStep = data.step;

    // default value for step status
    for (const item of formDefault.stepStatus) {
      const matched = data.stepStatus?.find((status) => status.id === item.id);
      if (!matched) continue;
      item.value = matched.value;
    }

    // default value for step 2
    const projectApplicant = {
      ...data.projectApplicant,
      dateOfBirth: convertStringToObjDate(data.projectApplicant.dateOfBirth),
      gender: GenderConverter.toOptionValue(data.projectApplicant?.gender),
      departureDate: TimeConverter.toDatePicker(data.projectApplicant.departureDate)
    };
    formDefault.steps[0] = {
      ...DEFAULT_SCENARIO_B_STEP_2,
      projectApplicant,
      projectAssignmentInfo: data.projectAssignmentInfo ?? {},
      visaCategoryName: data.visaCategoryName ?? null,
      travelTypeId: data.travelTypeId ?? null
    };

    // default value for step 3
    const projectFamilies = data.projectFamilies?.map((item) => {
      const newItem: any = { ...item };
      newItem.dateOfBirth = convertStringToObjDate(item.dateOfBirth);
      newItem.gender = GenderConverter.toOptionValue(newItem.gender);
      newItem.formId = uuid();
      return newItem;
    });
    formDefault.steps[1] = {
      ...DEFAULT_SCENARIO_B_STEP_3,
      familyAttendanceTime: data.familyAttendanceTime ?? null,
      projectFamilies: projectFamilies ?? []
    };

    // default value for step 4
    const needSupporter = NeedSupportRadioConverter.toOptionValue(data?.localSupportOption);
    const projectLocalSupporters = data.projectLocalSupporters?.map((item) => {
      const newItem: any = { ...item };
      newItem.formId = uuid();
      return newItem;
    });
    formDefault.steps[2] = {
      ...DEFAULT_SCENARIO_B_STEP_4,
      projectLocalSupporters: projectLocalSupporters ?? [],
      needSupporter
    };

    // default value for step 5
    let involvedMembers = null;
    if (data?.hasOtherStakeHolder === true) involvedMembers = INVOLVED_MEMBERS.YES;
    else if (data?.hasOtherStakeHolder === false) involvedMembers = INVOLVED_MEMBERS.NO;
    const projectStakeholders = [];
    const userProfile = getUserProfile();
    // ignore current user from stakeholders list for step 5
    for (const item of data?.projectStakeholders) {
      if (item.stakeholderId === userProfile?.id) continue;
      projectStakeholders.push(item);
    }
    formDefault.steps[3] = {
      ...DEFAULT_SCENARIO_B_STEP_5,
      projectStakeholders,
      involvedMembers
    };

    // default value for step 6
    const projectAttachments = data.attachments?.map((item) => {
      const newItem: any = { ...item };
      newItem.formId = uuid();
      newItem.status = UPLOAD_STATUS.DONE;
      return newItem;
    });
    formDefault.steps[4] = {
      ...DEFAULT_SCENARIO_B_STEP_6,
      projectAttachments: projectAttachments ?? [],
      note: data.note ?? ''
    };
  }

  return formDefault;
};

export const getCategoriesBy = (countryCode: string, countries?: ICountry[]) => {
  if (!countries) return [];
  const matched = countries.find((country) => country.id === countryCode);
  if (!matched?.categories) return [];
  return matched.categories.map((category) => ({
    label: category.name,
    value: category.id
  }));
};

export class GenderConverter {
  static toPayload(gender: any) {
    if (typeof gender === 'boolean') return gender;
    if (gender === APPLICANT_GENDER.FEMALE) return true;
    if (gender === APPLICANT_GENDER.MALE) return false;
    return null;
  }
  static toOptionValue(value?: boolean) {
    if (value === true) return APPLICANT_GENDER.FEMALE;
    if (value === false) return APPLICANT_GENDER.MALE;
    return null;
  }
}

export class TimeConverter {
  /**
   * @description Convert date string from response to value of date picker format
   * @param date - date string
   * @returns Date - dayjs format FORMAT_DATE_EN
   */
  static toDatePicker(date?: string | null) {
    if (!date) return null;
    return DataInput.formatTime(date);
  }
}

export class NeedSupportRadioConverter {
  static toPayload(value?: LOCAL_REPRESENTATIVE | null) {
    if (value === LOCAL_REPRESENTATIVE.NEEDED) return 1;
    if (value === LOCAL_REPRESENTATIVE.NOT_NEEDED) return 2;
    if (value === LOCAL_REPRESENTATIVE.UNKNOWN) return 3;
    return undefined;
  }

  static toOptionValue(value?: number) {
    if (value === 1) return LOCAL_REPRESENTATIVE.NEEDED;
    if (value === 2) return LOCAL_REPRESENTATIVE.NOT_NEEDED;
    if (value === 3) return LOCAL_REPRESENTATIVE.UNKNOWN;
    return null;
  }
}
export class ScenarioBodyConverter {
  private _data: any;
  private _formValues: FormObserver<'scenario-b'> | null = null;

  get data() {
    return this._data;
  }
  set data(newValue: any) {
    this._data = { ...this._data, ...newValue };
  }

  constructor(form: FormObserver<'scenario-b'>) {
    this._formValues = form;
    this._data = {};
  }

  basedOn(type: 'isDraft' | 'isPublished', projectDetails: IProjectExternal | null) {
    const { version, id } = projectDetails ?? {};
    if (type === 'isDraft') {
      this.data.isDraft = true;
      this.data.step = this._formValues?.selectedStep;
    } else {
      this.data.isDraft = false;
    }
    if (typeof version === 'number') {
      this.data.version = version;
    }
    if (id) this.data.id = id;
    // TODO: implement after BE ready
    this.data.stepStatus = this._formValues?.stepStatus?.map((item) => ({ id: item.id, value: item.value })) ?? [];
    return this;
  }

  attachFromStepTwoInfo() {
    const stepTwo = (this._formValues?.steps?.[0] as IStepTwo) ?? {};
    const { projectApplicant, projectAssignmentInfo, visaCategoryName, travelTypeId } = stepTwo;
    if (projectApplicant)
      this.data.projectApplicant = {
        ...projectApplicant,
        dateOfBirth: convertObjDatetoString(projectApplicant?.dateOfBirth),
        gender: GenderConverter.toPayload(projectApplicant?.gender),
        departureDate: DataInput.formatTime(projectApplicant?.departureDate)
      };
    if (projectAssignmentInfo) this.data.projectAssignmentInfo = projectAssignmentInfo;
    if (visaCategoryName) this.data.visaCategoryName = visaCategoryName;
    if (travelTypeId) this.data.travelTypeId = travelTypeId;
    return this;
  }

  attachFromStepThreeInfo(projectDetails: IProjectExternal | null) {
    const stepThree = (this._formValues?.steps?.[1] as IStepThree) ?? {};
    const { goWithFamily, projectFamilies, familyAttendanceTime } = stepThree;
    this.data.goWithFamily = Boolean(goWithFamily);
    let newPrjFamilies: IProjectExternal['projectFamilies'] = projectFamilies ?? [];

    if (projectDetails) {
      projectDetails.projectFamilies?.forEach((item) => {
        const isDeleted = item.id && !projectFamilies?.find((f) => f.id === item.id);
        if (!isDeleted) return;
        newPrjFamilies.push({ ...item, deleted: true });
      });
    }

    for (const index in newPrjFamilies) {
      const item = newPrjFamilies[index] as Partial<IProjectFamily> & { formId?: string };
      delete item?.formId;
      if (item.dateOfBirth) item.dateOfBirth = convertObjDatetoString(item.dateOfBirth) ?? undefined;
      if (item.gender) item.gender = GenderConverter.toPayload(item.gender);
      item.displayOrder = Number(index);
    }

    this.data.projectFamilies = newPrjFamilies;
    this.data.familyAttendanceTime = familyAttendanceTime;
    return this;
  }

  attachFromStepFourInfo(projectDetails: IProjectExternal | null) {
    const stepFour = (this._formValues?.steps?.[2] as IStepFour) ?? {};
    const { needSupporter, projectLocalSupporters } = stepFour;

    this.data.localSupportOption = NeedSupportRadioConverter.toPayload(needSupporter);

    let newPrjLocalSupporters: IProjectExternal['projectLocalSupporters'] = projectLocalSupporters ?? [];
    if (projectDetails) {
      projectDetails.projectLocalSupporters?.forEach((item) => {
        const isDeleted = item.id && !projectLocalSupporters?.find((f) => f.id === item.id);
        if (!isDeleted) return;
        newPrjLocalSupporters.push({ ...item, deleted: true });
      });
    }

    let language = newPrjLocalSupporters[0]?.language ?? undefined;
    for (const index in newPrjLocalSupporters) {
      const item = newPrjLocalSupporters[index];
      item.language = language;
      item.displayOrder = Number(index);
    }

    this.data.projectLocalSupporters = newPrjLocalSupporters;
    return this;
  }

  attachFromStepFiveInfo(projectDetails: IProjectExternal | null) {
    const stepFive = (this._formValues?.steps?.[3] as IStepFive) ?? {};
    const { involvedMembers, projectStakeholders } = stepFive;
    if (involvedMembers === INVOLVED_MEMBERS.YES) this.data.hasOtherStakeHolder = true;
    else if (involvedMembers === INVOLVED_MEMBERS.NO) this.data.hasOtherStakeHolder = false;

    const uniqueStakeholders = lodash.uniqWith(
      projectStakeholders?.filter((item: IProjectStakeholder) => item.stakeholderEmail),
      (accountA: IProjectStakeholder, accountB: IProjectStakeholder) =>
        accountA.stakeholderEmail === accountB.stakeholderEmail && !accountA.rejected && !accountB.rejected
    );
    const newPrjStakeholders: IProjectExternal['projectStakeholders'] = uniqueStakeholders.map((item: any, index: number) => ({
      ...item,
      displayOrder: index + 1,
      deleted: item?.deleted ?? false
    }));

    const userProfile = getUserProfile();
    projectDetails?.projectStakeholders?.forEach((item) => {
      // ignore current user from stakeholders list for step 5
      if (item.stakeholderId === userProfile?.id) return;
      const isDeleted = item.stakeholderEmail && item.id && !newPrjStakeholders.find((f) => f.id === item.id);
      if (!isDeleted) return;
      newPrjStakeholders.push({ ...item, deleted: true });
    });

    this.data.projectStakeholders = newPrjStakeholders ?? [];

    return this;
  }

  attachFromStepSixInfo(projectDetails: IProjectExternal | null) {
    const stepSix = (this._formValues?.steps?.[4] as IStepSix) ?? {};
    const { projectAttachments, note } = stepSix;

    const newPrjAttachments = [];

    for (const index in projectAttachments) {
      const file = projectAttachments[index];
      if (file.status !== 'done') continue;
      file.displayOrder = Number(index);
      file.deleted = false;
      newPrjAttachments.push(file);
    }
    const defaultAttachments = projectDetails?.attachments ?? projectDetails?.projectAttachments;
    defaultAttachments?.forEach((item) => {
      const isDeleted = !projectAttachments.find((i: any) => i.id === item.id) && item.id;
      if (!isDeleted) return;
      newPrjAttachments.push({ ...item, deleted: true });
    });

    this.data.projectAttachments = newPrjAttachments;
    this.data.note = DataViewer.isEmptyHTML(note) ? '' : note;
    return this;
  }

  sanitize() {
    const loop = (data: { [key in string | number]: any }) => {
      for (const key in data) {
        if (data[key] === null) {
          delete data[key];
        } else if (data[key] instanceof Array) {
          data[key].forEach(loop);
        } else if (typeof data[key] === 'object') {
          loop(data[key]);
        }
      }
    };
    loop(this.data);
    return this;
  }

  embedToJSON() {
    return {
      command: JSON.stringify(this.data)
    };
  }
}

export class DirectorConverter {
  private _builder: ScenarioBodyConverter | null = null;
  constructor(builder: ScenarioBodyConverter) {
    this._builder = builder;
  }

  processDraft(projectDetails: IProjectExternal | null) {
    if (!this._builder) return this;
    this._builder
      .basedOn('isDraft', projectDetails)
      .attachFromStepTwoInfo()
      .attachFromStepThreeInfo(projectDetails)
      .attachFromStepFourInfo(projectDetails)
      .attachFromStepFiveInfo(projectDetails)
      .attachFromStepSixInfo(projectDetails);
    return this;
  }

  processPublished(projectDetails: IProjectExternal | null) {
    if (!this._builder) return this;
    this._builder
      .basedOn('isPublished', projectDetails)
      .attachFromStepTwoInfo()
      .attachFromStepThreeInfo(projectDetails)
      .attachFromStepFourInfo(projectDetails)
      .attachFromStepFiveInfo(projectDetails)
      .attachFromStepSixInfo(projectDetails);
    return this;
  }

  getJSON() {
    return this._builder ? this._builder.sanitize().embedToJSON() : null;
  }
}

export class FormFieldObserver {
  private _formValues: FormObserver<'scenario-b'> | null = null;
  constructor(formValues: FormObserver<'scenario-b'>) {
    this._formValues = formValues;
  }

  isValidAt(stepId: number, formErrors: FieldErrors<FormObserver<'scenario-b'>>): boolean {
    const stepIndex = stepId - STEP_JUMP;
    let errCurrentStep;
    if (stepIndex >= 0) {
      errCurrentStep = get(formErrors, `steps[${stepIndex}]`);
    }
    return Boolean(errCurrentStep);
  }

  requiredFilledAllAt(stepId: number): boolean {
    switch (stepId) {
      case 2:
        return this.requiredFilledAtStepTwo();
      case 3:
        return this.requiredFilledAtStepThree();
      case 4:
        return this.requiredFilledAtStepFour();
      case 5:
        return this.requiredFilledAtStepFive();
      case 6:
        return true;
      case 7:
        return true;
      default:
        return false;
    }
  }

  requiredFilledAtStepTwo(): boolean {
    const stepTwo = this._formValues?.steps?.[0] as IStepTwo;
    if (!stepTwo) return false;
    const requiredFields = [
      ...REQUIRED_APPLICANT_FIELDS.map((field) => relativePath(field, 'projectApplicant')),
      ...REQUIRED_ASSIGNMENT_FIELDS.map((field) => relativePath(field, 'projectAssignmentInfo'))
    ];
    for (const fieldPath of requiredFields) {
      const fieldValue = get(stepTwo, fieldPath);
      if (!fieldValue) return false;
    }
    return true;
  }

  private _detectFilledAllRequiredForArray<T>(array: T[], requiredFields: string[]): boolean {
    if (!array) return false;
    for (const member of array) {
      const missingField = keys(member).find((key) => {
        const isFieldRequired = requiredFields.includes(key);
        const fieldValue = member?.[key as keyof T];
        return isFieldRequired && !fieldValue;
      });
      if (!missingField) continue;
      return false;
    }
    return true;
  }

  requiredFilledAtStepThree(): boolean {
    const stepThree = (this._formValues?.steps?.[1] as IStepThree) ?? {};
    if (!stepThree.goWithFamily) return true;
    if (!stepThree.projectFamilies) return false;
    return this._detectFilledAllRequiredForArray(stepThree.projectFamilies, REQUIRED_FAMILY_FIELDS);
  }

  requiredFilledAtStepFour(): boolean {
    const stepFour = (this._formValues?.steps?.[2] as IStepFour) ?? {};
    if (stepFour.needSupporter === LOCAL_REPRESENTATIVE.NOT_NEEDED) return true;
    if (!stepFour.projectLocalSupporters || !stepFour.needSupporter) return false;
    return this._detectFilledAllRequiredForArray(stepFour.projectLocalSupporters, REQUIRED_LOCAL_REPRESENTATIVE_FIELDS);
  }

  requiredFilledAtStepFive(): boolean {
    const stepFive = (this._formValues?.steps?.[3] as IStepFive) ?? {};
    if (stepFive.involvedMembers === INVOLVED_MEMBERS.NO) return true;
    if (!stepFive.projectStakeholders || stepFive.involvedMembers === null) return false;
    return this._detectFilledAllRequiredForArray(stepFive.projectStakeholders, REQUIRED_STAKEHOLDER_FIELDS);
  }
}

export const isEmail = (email: string) => {
  return email && EMAIL_REGEX.test(email);
};

export const getValidEmailStakeholders = (projectStakeholders: IProjectExternal['projectStakeholders']): string[] => {
  const emailAddress: string[] = [];
  for (const item of projectStakeholders) {
    if (!item.stakeholderEmail || !isEmail(item.stakeholderEmail)) continue;
    emailAddress.push(item.stakeholderEmail);
  }
  return emailAddress;
};
